// src/pages/Login.tsx (Updated)
import React, { useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Avatar, Button, TextField, Box, Typography, Container, Alert, Grid, Link, ToggleButton, ToggleButtonGroup } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import { API_BASE_URL } from '../utils/api';

const Login: React.FC = () => {
  const [loginMethod, setLoginMethod] = useState<'email' | 'phone'>('email');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');
    
    // Validate that the appropriate field is filled
    if (loginMethod === 'email' && !email) {
      setError('Please enter your email address.');
      return;
    }
    if (loginMethod === 'phone' && !phone) {
      setError('Please enter your phone number.');
      return;
    }
    if (!password) {
      setError('Please enter your password.');
      return;
    }
    
    setLoading(true);
    try {
      const loginData = loginMethod === 'email' 
        ? { email, password }
        : { phone, password };
      
      const url = `${API_BASE_URL}/login`;
      console.log('[Login] Posting to:', url);
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(loginData),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Login failed.');
      }
      login(data.token, data.user); // Use the context login function
      navigate('/');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLoginMethodChange = (
    event: React.MouseEvent<HTMLElement>,
    newMethod: 'email' | 'phone' | null,
  ) => {
    if (newMethod !== null) {
      setLoginMethod(newMethod);
      setError(''); // Clear any existing errors
    }
  };

  return (
    <Container component="main" maxWidth="xs">
      <Box sx={{ marginTop: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Avatar sx={{ m: 1, bgcolor: 'secondary.main' }}><LockOutlinedIcon /></Avatar>
        <Typography component="h1" variant="h5">Sign in</Typography>
        
        <Box sx={{ mt: 2, mb: 2 }}>
          <ToggleButtonGroup
            value={loginMethod}
            exclusive
            onChange={handleLoginMethodChange}
            aria-label="login method"
          >
            <ToggleButton value="email" aria-label="email">
              <EmailIcon sx={{ mr: 1 }} />
              Email
            </ToggleButton>
            <ToggleButton value="phone" aria-label="phone">
              <PhoneIcon sx={{ mr: 1 }} />
              Phone
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
        
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1, width: '100%' }}>
          {loginMethod === 'email' ? (
            <TextField
              margin="normal"
              required
              fullWidth
              label="Email Address"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={e => setEmail(e.target.value)}
            />
          ) : (
            <TextField
              margin="normal"
              required
              fullWidth
              label="Phone Number"
              autoComplete="tel"
              autoFocus
              value={phone}
              onChange={e => setPhone(e.target.value)}
              placeholder="+1234567890"
            />
          )}
          <TextField
            margin="normal"
            required
            fullWidth
            label="Password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={e => setPassword(e.target.value)}
          />
          {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
          <Button type="submit" fullWidth variant="contained" sx={{ mt: 3, mb: 2 }} disabled={loading}>
            {loading ? 'Signing in...' : 'Sign In'}
          </Button>
          <Grid container>
            <Grid item>
              <Link component={RouterLink} to="/register" variant="body2">
                {"Don't have an account? Sign Up"}
              </Link>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Container>
  );
};

export default Login;